const ROLE_PREFIXES = ["STANDARD", "CASHIER", "AUDITOR", "MANAGER"];
const ACTIONS = [
  "MANAGE",
  "READ",
  "VIEW",
  "WRITE",
  "CREATE",
  "EDIT",
  "CHANGE",
  "PROCESS",
  "SHARE",
  "LOCK",
  "UNLOCK",
  "APPROVE",
  "TRANSFER",
  "RELEASE",
];
const ACTION_ORDER = {
  READ: 0,
  VIEW: 0,
  WRITE: 1,
  CREATE: 1,
  EDIT: 1,
  MANAGE: 2,
};

const SPECIAL_RESOURCES = {
  TICKET_CARTS: {
    title: "Ticket carts",
    subject: "ticket carts",
  },
  EXPENSES: {
    title: "Expenses",
    subject: "expenses",
  },
  SALE_CARTS: {
    title: "Sale carts",
    subject: "sale carts",
  },
};

const RESOURCE_MATCHERS = [
  ["TICKET_CARTS", "TICKET_CARTS"],
  ["SALE_CARTS", "SALE_CARTS"],
  ["STORE_SETTINGS", "STORE_SETTINGS"],
  ["PURCHASE_SUPPLIERS", "PURCHASE_SUPPLIERS"],
  ["PURCHASE_ORDERS", "PURCHASE_ORDERS"],
  ["PURCHASE_RECEIPTS", "PURCHASE_RECEIPTS"],
  ["PROMO_CODES", "PROMO_CODES"],
  ["RRA_EBM_DATA", "RRA_EBM_DATA"],
  ["EBM_SETTINGS", "EBM_SETTINGS"],
  ["EXPENSE", "EXPENSES"],
  ["PURCHASE", "PURCHASES"],
  ["ASSET", "ASSETS"],
  ["DASHBOARD", "DASHBOARDS"],
  ["PRODUCT", "PRODUCTS"],
  ["SERVICE", "SERVICES"],
  ["REPORT", "REPORTS"],
  ["CUSTOMER", "CUSTOMERS"],
  ["REFUND", "REFUNDS"],
  ["USER", "USERS"],
  ["INVENTORY", "INVENTORY"],
  ["OPERATIONAL_UNIT", "OPERATIONAL_UNITS"],
];

const titleCase = (value = "") =>
  String(value)
    .toLowerCase()
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const normalizePermissionName = (name = "") => {
  let normalized = String(name || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  const canIndex = normalized.indexOf("CAN_");

  if (canIndex >= 0) {
    return normalized.slice(canIndex);
  }

  let parts = normalized.split("_").filter(Boolean);
  while (ROLE_PREFIXES.includes(parts[0])) {
    parts = parts.slice(1);
  }
  normalized = parts.join("_");

  return normalized;
};

const parsePermission = (permission) => {
  const normalized = normalizePermissionName(permission);
  const body = normalized.replace(/^CAN_/, "");
  const action = ACTIONS.find(
    (candidate) => body === candidate || body.startsWith(`${candidate}_`),
  );

  const rawResource = action ? body.slice(action.length + 1) : body;
  const matchedResource = RESOURCE_MATCHERS.find(([fragment]) =>
    rawResource.includes(fragment),
  );

  return {
    action: action || "",
    resource: matchedResource?.[1] || rawResource,
    rawResource,
    value: normalized,
  };
};

const resourceTitle = (resource) =>
  SPECIAL_RESOURCES[resource]?.title || titleCase(resource);

const permissionMetadata = (permission, fallbackLabel = "") => {
  const { action, resource, value } = parsePermission(permission);
  const special = SPECIAL_RESOURCES[resource];

  if (special && ["READ", "VIEW"].includes(action)) {
    return {
      value,
      label: "View own",
      description: `View your own ${special.subject}.`,
    };
  }
  if (special && ["WRITE", "CREATE", "EDIT"].includes(action)) {
    return {
      value,
      label: "Create/edit own",
      description: `Create new and edit your own ${special.subject}.`,
    };
  }
  if (special && action === "MANAGE") {
    return {
      value,
      label: "Full store access",
      description: `View and edit all ${special.subject} in this store.`,
    };
  }

  const title = resourceTitle(resource);
  const actionMetadata = {
    READ: {
      label: `View ${title.toLowerCase()}`,
      description: `View ${title.toLowerCase()} for this store.`,
    },
    VIEW: {
      label: `View ${title.toLowerCase()}`,
      description: `View ${title.toLowerCase()} for this store.`,
    },
    WRITE: {
      label: `Create/edit ${title.toLowerCase()}`,
      description: `Create and edit ${title.toLowerCase()} for this store.`,
    },
    CREATE: {
      label: `Create ${title.toLowerCase()}`,
      description: `Create ${title.toLowerCase()} for this store.`,
    },
    EDIT: {
      label: `Edit ${title.toLowerCase()}`,
      description: `Edit ${title.toLowerCase()} for this store.`,
    },
    MANAGE: {
      label: `Manage ${title.toLowerCase()}`,
      description: `View, create, and edit all ${title.toLowerCase()} for this store.`,
    },
    RELEASE: {
      label: `Release stuck ${title.toLowerCase()}`,
      description: `Release abandoned active assignments from ${title.toLowerCase()} with an audit reason.`,
    },
  };

  return {
    value,
    label:
      fallbackLabel ||
      actionMetadata[action]?.label ||
      titleCase(value.replace(/^CAN_/, "")),
    description:
      actionMetadata[action]?.description ||
      `Allows ${titleCase(value.replace(/^CAN_/, "")).toLowerCase()}.`,
  };
};

const catalogEntries = (catalog) => {
  if (Array.isArray(catalog)) {
    return catalog;
  }
  if (!catalog || typeof catalog !== "object") {
    return [];
  }
  return Object.values(catalog).flatMap((entries) =>
    Array.isArray(entries) ? entries : [],
  );
};

export const groupPermissionCatalog = (catalog) => {
  const optionsByValue = new Map();

  catalogEntries(catalog).forEach((entry) => {
    const source = typeof entry === "string" ? { name: entry } : entry;
    const value = normalizePermissionName(
      source?.standardized_name || source?.name,
    );
    if (!value || optionsByValue.has(value)) {
      return;
    }
    optionsByValue.set(value, permissionMetadata(value, source?.label));
  });

  const groups = new Map();
  optionsByValue.forEach((option) => {
    const parsed = parsePermission(option.value);
    const key = parsed.resource || "OTHER";
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: resourceTitle(key) || "Other permissions",
        options: [],
      });
    }
    groups.get(key).options.push(option);
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      options: group.options.sort((left, right) => {
        const leftAction = parsePermission(left.value).action;
        const rightAction = parsePermission(right.value).action;
        return (
          (ACTION_ORDER[leftAction] ?? 10) -
            (ACTION_ORDER[rightAction] ?? 10) ||
          left.label.localeCompare(right.label)
        );
      }),
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
};

export const getPermissionDependencies = (permission) => {
  const { action, resource } = parsePermission(permission);
  if (action !== "MANAGE" || !resource) {
    return [];
  }
  return [`CAN_READ_${resource}`, `CAN_WRITE_${resource}`];
};

export const closePermissionDependencies = (permissions = []) => {
  const closed = new Set(
    permissions.map(normalizePermissionName).filter(Boolean),
  );

  Array.from(closed).forEach((permission) => {
    getPermissionDependencies(permission).forEach((dependency) =>
      closed.add(dependency),
    );
  });

  return Array.from(closed);
};

export const updatePermissionSelection = (
  permissions,
  permission,
  selected,
  availablePermissions = [],
) => {
  const normalized = normalizePermissionName(permission);
  const next = new Set(
    permissions.map(normalizePermissionName).filter(Boolean),
  );

  if (selected) {
    next.add(normalized);
    return closePermissionDependencies(Array.from(next));
  }

  next.delete(normalized);
  const removed = parsePermission(normalized);
  if (removed.action === "MANAGE") {
    const available = new Set(
      availablePermissions.map(normalizePermissionName).filter(Boolean),
    );
    getPermissionDependencies(normalized).forEach((dependency) => {
      if (!available.has(dependency)) {
        next.delete(dependency);
      }
    });
  }
  if (["READ", "WRITE"].includes(removed.action)) {
    next.delete(`CAN_MANAGE_${removed.resource}`);
  }
  return Array.from(next);
};

export const getPermissionLabel = (permission) =>
  permissionMetadata(permission).label;

export const getPermissionSummaryLabel = (permission) => {
  const metadata = permissionMetadata(permission);
  const { resource } = parsePermission(permission);
  return SPECIAL_RESOURCES[resource]
    ? `${resourceTitle(resource)}: ${metadata.label}`
    : metadata.label;
};
